import axios from 'axios';
import { consentPayload } from './legal';
import Cookies from 'js-cookie';
import { API_URL } from './api';

// ============================================================
//  Хранилище токенов (куки)
// ============================================================
const ACCESS_KEY = 'access';
const REFRESH_KEY = 'refresh';
const LEGACY_KEY = 'authToken'; // старый ключ (telegram id / jwt из URL)

// Активную сессию храним per-вкладку в sessionStorage (изоляция вкладок, баг #10),
// а в куках — как «запомнить» на будущие запуски. sessionStorage приоритетнее при чтении:
// логин в другой вкладке не перебивает сессию текущей.
const ss = () => (typeof window !== 'undefined' ? window.sessionStorage : null);

export const getAccessToken = () =>
  ss()?.getItem(ACCESS_KEY) || Cookies.get(ACCESS_KEY) || Cookies.get(LEGACY_KEY) || null;
export const getRefreshToken = () =>
  ss()?.getItem(REFRESH_KEY) || Cookies.get(REFRESH_KEY) || null;

export const setTokens = ({ access, refresh }) => {
  if (access) { ss()?.setItem(ACCESS_KEY, access); Cookies.set(ACCESS_KEY, access, { expires: 7 }); }
  if (refresh) { ss()?.setItem(REFRESH_KEY, refresh); Cookies.set(REFRESH_KEY, refresh, { expires: 30 }); }
};

export const clearTokens = () => {
  ss()?.removeItem(ACCESS_KEY);
  ss()?.removeItem(REFRESH_KEY);
  Cookies.remove(ACCESS_KEY);
  Cookies.remove(REFRESH_KEY);
  Cookies.remove(LEGACY_KEY);
};

// ============================================================
//  Прозрачный refresh по 401 (axios-интерцептор)
// ============================================================
let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (error, newAccess = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newAccess);
  });
  pendingQueue = [];
};

export const installAuthInterceptor = ({ onAuthFail, onRefreshed } = {}) => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      // не трогаем не-401, повторы и сам refresh-запрос
      if (
        status !== 401 ||
        !original ||
        original.skipAuthRefresh === true ||
        original._retry ||
        (original.url && original.url.includes('/auth/refresh/'))
      ) {
        return Promise.reject(error);
      }

      const refresh = getRefreshToken();
      if (!refresh) {
        onAuthFail?.();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // ждём завершения текущего refresh, затем повторяем запрос
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((newAccess) => {
          original._retry = true;
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newAccess}`;
          return axios(original);
        });
      }

      original._retry = true;
      isRefreshing = true;
      try {
        const resp = await axios.post(`${API_URL}/api/main/auth/refresh/`, { refresh });
        const newAccess = resp.data.access;
        const newRefresh = resp.data.refresh;
        setTokens({ access: newAccess, refresh: newRefresh });
        onRefreshed?.(newAccess);
        flushQueue(null, newAccess);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return axios(original);
      } catch (e) {
        flushQueue(e, null);
        clearTokens();
        onAuthFail?.();
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

// ============================================================
//  Хелперы авторизации / регистрации / сессий
// ============================================================
const jsonCfg = () => ({ headers: { 'Content-Type': 'application/json' } });
const authCfg = () => ({
  headers: { Authorization: `Bearer ${getAccessToken()}`, 'Content-Type': 'application/json' },
});

export const apiDoctorLogin = async ({ phone, password, device_name, consentAccepted }) => {
  const resp = await axios.post(`${API_URL}/api/main/auth/doctor/login/`,
    { phone, password, device_name, ...consentPayload(consentAccepted) },
    { ...jsonCfg(), skipAuthRefresh: true });
  return resp.data;
};

export const apiDoctorConfirmationRequest = async ({ challenge, channel, consentAccepted }) => {
  const resp = await axios.post(`${API_URL}/api/main/auth/doctor/confirmation/request/`,
    { challenge, channel, ...consentPayload(consentAccepted) },
    { ...jsonCfg(), skipAuthRefresh: true });
  return resp.data;
};

export const apiDoctorConfirmationVerify = async ({ challenge, code, consentAccepted }) => {
  const resp = await axios.post(`${API_URL}/api/main/auth/doctor/confirmation/verify/`,
    { challenge, code, ...consentPayload(consentAccepted) },
    { ...jsonCfg(), skipAuthRefresh: true });
  // Токены сохранит компонент, только если он ещё относится к текущему входу.
  return resp.data;
};

export const apiPatientLogin = async ({ phone, password, device_name, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/patient/login/`,
    { phone, password, device_name, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  if (resp.data?.access) setTokens({ access: resp.data.access, refresh: resp.data.refresh });
  return resp.data;
};

export const apiRequestCode = async ({ role, channel, destination, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/code/request/`,
    { role, channel, destination, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  return resp.data;
};

export const apiPasswordResetRequest = async ({ role, channel, destination, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/password/reset/request/`,
    { role, channel, destination, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  return resp.data;
};

export const apiPasswordResetConfirm = async ({ role, channel, destination, code, new_password, device_name, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/password/reset/confirm/`,
    { role, channel, destination, code, new_password, device_name, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  if (resp.data?.access) setTokens({ access: resp.data.access, refresh: resp.data.refresh });
  return resp.data;
};

export const apiVerifyCode = async ({ role, channel, destination, code, device_name, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/code/verify/`,
    { role, channel, destination, code, device_name, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  if (resp.data?.access) setTokens({ access: resp.data.access, refresh: resp.data.refresh });
  return resp.data;
};

export const apiPatientRegister = async ({ mode, contact, doctor_token, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/patient/register/`,
    { mode, contact, doctor_token, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  // На этом шаге нет токенов: лишь ticket с TTL 15 минут.
  return resp.data;
};

export const apiPatientRegisterRequestCode = async ({ ticket, channel, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/patient/register/code/request/`,
    { ticket, channel, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  return resp.data;
};

export const apiPatientRegisterVerifyCode = async ({ ticket, channel, code, device_name, name, consentAccepted }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/patient/register/code/verify/`,
    { ticket, channel, code, device_name, name, ...consentPayload(consentAccepted) },
    jsonCfg()
  );
  if (resp.data?.access) setTokens({ access: resp.data.access, refresh: resp.data.refresh });
  return resp.data;
};

export const apiGetMe = async () => {
  const resp = await axios.get(`${API_URL}/api/main/auth/me/`, authCfg());
  return resp.data;
};

export const apiLogout = async () => {
  try {
    await axios.post(`${API_URL}/api/main/auth/logout/`, {}, authCfg());
  } finally {
    clearTokens();
  }
};

export const apiGetSessions = async () => {
  const resp = await axios.get(`${API_URL}/api/main/auth/sessions/`, authCfg());
  return resp.data;
};

export const apiRevokeSession = async (sessionId) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/sessions/${sessionId}/revoke/`,
    {},
    authCfg()
  );
  return resp.data;
};

export const apiRevokeAllSessions = async (includeCurrent = false) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/sessions/revoke-all/`,
    { include_current: includeCurrent },
    authCfg()
  );
  return resp.data;
};

export const apiChangePassword = async ({ old_password, new_password }) => {
  const resp = await axios.post(
    `${API_URL}/api/main/auth/change-password/`,
    { old_password, new_password },
    authCfg()
  );
  return resp.data;
};

export const apiSelectDoctor = async (doctorId) => {
  const resp = await axios.post(
    `${API_URL}/api/main/patient/select-doctor/`,
    { doctor_id: doctorId },
    authCfg()
  );
  return resp.data;
};